import { useMutation } from "@tanstack/react-query";
import type { User as FirebaseUser } from "firebase/auth";
import {
	signOut as firebaseSignOut,
	onAuthStateChanged,
	signInWithPopup,
	signInWithRedirect,
	getRedirectResult,
} from "firebase/auth";
import { toast } from "sonner";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";
import authService from "@/api/services/authService";
import userService, { type SignInReq } from "@/api/services/userService";
import { auth, googleProvider } from "@/lib/firebase";
import type { UserRole } from "@/types/entity";

/**
 * 检查用户角色是否为 "user"，如果是则拒绝登录
 */
const checkUserRole = (userInfo: UserInfo): boolean => {
	const userRole = userInfo.role || userInfo.roles?.[0];
	if (userRole === "user") {
		return false;
	}
	return true;
};

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	firebaseUser: FirebaseUser | null;
	loading: boolean;
	error: string | null;

	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		clearUserInfoAndToken: () => void;
		signInWithGoogle: () => Promise<void>;
		initAuth: () => void | Promise<void>;
		signOut: () => Promise<void>;
		clearError: () => void;
	};
};

const useUserStore = create<UserStore>()(
	persist(
		(set, get) => ({
			userInfo: {},
			userToken: {},
			firebaseUser: null,
			loading: false,
			error: null,
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					set({ userToken });
				},
				clearUserInfoAndToken() {
					set({ userInfo: {}, userToken: {}, firebaseUser: null });
				},
				clearError: () => {
					set({ error: null });
				},
				initAuth: async () => {
					// 生产环境：先处理重定向返回结果，再注册 onAuthStateChanged
					if (import.meta.env.PROD) {
						try {
							await getRedirectResult(auth);
						} catch (error: any) {
							console.error("Redirect result error:", error);
							set({
								error: error?.message || "重定向登录失败",
								loading: false,
							});
							toast.error(error?.message || "重定向登录失败", {
								position: "top-center",
							});
						}
					}
					onAuthStateChanged(auth, async (user) => {
						if (user) {
							try {
								const idToken = await user.getIdToken();
								set({ firebaseUser: user, loading: false });

								// 如果还没有后端 token，尝试验证
								if (!get().userToken.accessToken) {
									try {
										const res = await authService.verifyFirebaseToken(idToken);
										if (res.token) {
											get().actions.setUserToken({ accessToken: res.token });
											// 验证成功后获取用户信息
											try {
												const userInfo = await userService.getCurrentUser();
												// 检查用户角色，如果是 "user" 则拒绝登录
												if (!checkUserRole(userInfo)) {
													await firebaseSignOut(auth);
													get().actions.clearUserInfoAndToken();
													set({
														error: "您的账户没有权限访问管理系统，请联系管理员",
														loading: false,
													});
													toast.error("您的账户没有权限访问管理系统", {
														position: "top-center",
													});
													// 导航到登录页面
													window.location.replace("/auth/login");
													return;
												}
												get().actions.setUserInfo({
													...userInfo,
													roles: [userInfo.role as UserRole],
												});
											} catch (error) {
												console.error("Failed to get user info:", error);
											}
										}
									} catch (error) {
										console.error("Failed to verify Firebase token:", error);
									}
								} else {
									// 如果已经有 token 但还没有用户信息，则获取用户信息
									if (!get().userInfo.id) {
										try {
											const userInfo = await userService.getCurrentUser();
											// 检查用户角色，如果是 "user" 则拒绝登录
											if (!checkUserRole(userInfo)) {
												await firebaseSignOut(auth);
												get().actions.clearUserInfoAndToken();
												set({
													error: "您的账户没有权限访问管理系统，请联系管理员",
													loading: false,
												});
												toast.error("您的账户没有权限访问管理系统", {
													position: "top-center",
												});
												// 导航到登录页面
												window.location.replace("/auth/login");
												return;
											}
											get().actions.setUserInfo(userInfo);
										} catch (error) {
											console.error("Failed to get user info:", error);
										}
									}
								}
							} catch (error) {
								console.error("Failed to get idToken:", error);
								set({ firebaseUser: user, loading: false });
							}
						} else {
							set({ firebaseUser: null });
							get().actions.clearUserInfoAndToken();
						}
					});
				},
				signInWithGoogle: async () => {
					set({ loading: true, error: null });
					try {
						// 先退出 Firebase 再登录
						if (auth.currentUser) {
							await firebaseSignOut(auth);
						}
						// 本地/开发环境使用 popup，生产环境使用 redirect（与 42x-card-frontend 一致）
						const isDev = import.meta.env.DEV;
						if (isDev) {
							// 开发环境：使用 popup 登录
							const result = await signInWithPopup(auth, googleProvider);
							const idToken = await result.user.getIdToken();
							set({ firebaseUser: result.user });

							// 验证 Firebase token 并登录到后端
							try {
								const res = await authService.verifyFirebaseToken(idToken);
								if (res.token) {
									get().actions.setUserToken({ accessToken: res.token });
									try {
										const userInfo = await userService.getCurrentUser();
										if (!checkUserRole(userInfo)) {
											await firebaseSignOut(auth);
											get().actions.clearUserInfoAndToken();
											set({
												error: "您的账户没有权限访问管理系统，请联系管理员",
												loading: false,
											});
											toast.error("您的账户没有权限访问管理系统", {
												position: "top-center",
											});
											window.location.replace("/auth/login");
											throw new Error("用户角色没有权限");
										}
										get().actions.setUserInfo({
											...userInfo,
											roles: [userInfo.role as UserRole],
										});
									} catch (userInfoError: any) {
										if (userInfoError.message === "用户角色没有权限") {
											throw userInfoError;
										}
										console.error("Failed to get user info:", userInfoError);
									}
									set({ loading: false });
									toast.success("登录成功", { position: "top-center" });
								} else {
									throw new Error(res.message || "后端验证失败");
								}
							} catch (backendError: any) {
								console.error("Backend verify error:", backendError);
								set({
									error: `登录成功，但连接服务器失败: ${backendError.message}`,
									loading: false,
								});
								toast.error(backendError.message || "后端验证失败", {
									position: "top-center",
								});
							}
						} else {
							// 生产环境：使用 redirect 登录，页面会跳转，返回后由 getRedirectResult + onAuthStateChanged 处理
							await signInWithRedirect(auth, googleProvider);
						}
					} catch (error: any) {
						console.error("Google sign in error:", error);
						const errorMessage = error.message || "登录失败，请重试";
						set({
							error: errorMessage,
							loading: false,
						});
						toast.error(errorMessage, {
							position: "top-center",
						});
						throw error;
					}
				},
				signOut: async () => {
					set({ loading: true, error: null });
					try {
						await firebaseSignOut(auth);
						get().actions.clearUserInfoAndToken();
						set({ loading: false });
					} catch (error: any) {
						console.error("Sign out error:", error);
						set({
							error: error.message || "登出失败，请重试",
							loading: false,
						});
						toast.error(error.message || "登出失败", {
							position: "top-center",
						});
						throw error;
					}
				},
			},
		}),
		{
			name: "userStore", // name of the item in the storage (must be unique)
			storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
			}),
		},
	),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useUserPermissions = () => useUserStore((state) => state.userInfo.permissions || []);
export const useUserRoles = () => useUserStore((state) => state.userInfo.roles || []);
export const useUserActions = () => useUserStore((state) => state.actions);
export { useUserStore };

export const useSignIn = () => {
	const { setUserToken, setUserInfo } = useUserActions();

	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);
			const { user, accessToken, refreshToken } = res;
			// 检查用户角色，如果是 "user" 则拒绝登录
			if (!checkUserRole(user)) {
				toast.error("您的账户没有权限访问管理系统，请联系管理员", {
					position: "top-center",
				});
				// 导航到登录页面
				window.location.replace("/auth/login");
				throw new Error("用户角色没有权限");
			}
			setUserToken({ accessToken, refreshToken });
			setUserInfo(user);
		} catch (err) {
			toast.error(err.message, {
				position: "top-center",
			});
			throw err;
		}
	};

	return signIn;
};

export default useUserStore;
